import { useEventCallback } from '@flippingbook/web.react-hooks/dist/useEventCallback';
import { Product } from '@flippingbook/web.utils/dist/ProductsList';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import type { To } from 'react-router-dom';

import { useCaptcha } from '@/hooks/useCaptcha';
import { loc } from '@/localization';
import { optionsService } from '@/services/instance/optionsService';
import { sso } from '@/services/instance/sso';
import { SignupResult } from '@/services/sso';
import { getLoginUrl, getRestoreUrl } from '@/utils/getPageUrl';
import { captcha, email, name, registerPassword } from '@/utils/validationRules';

export const validationRules = {
    email,
    captcha,
    password: registerPassword,
    firstName: name,
    lastName: name,
} as const;

type SignupApiRequest = Parameters<(typeof sso)['register']>[0];
export type FormDataType = Omit<SignupApiRequest, 'csrfToken' | 'captchaId'>;

function parseQueryProduct(queryProducts: string | null | undefined): Product {
    const products = queryProducts ? queryProducts.split(',') : [Product.FBOnline];

    if (products[0].toLowerCase().includes('publisher2')) {
        return Product.Publisher2;
    }
    return Product.FBOnline;
}

// WEB-9085 don't show captcha initially, even if it is required
// const initialCaptchaVisible = optionsService.getCaptchaRequired();
const initialCaptchaVisible = false;

interface Params {
    captchaWidth?: number;
    captchaHeight?: number;
    onLogin?: (() => Promise<void>) | (() => void);
    onRegister?: (() => Promise<void>) | (() => void);
    onShowCaptcha?: ((attempts: number) => Promise<void>) | ((attempts: number) => void);
}

export function useRegisterPage(params: Params = {}) {
    const { captchaWidth, captchaHeight, onLogin, onRegister, onShowCaptcha } = params;

    const [hasLoginError, setHasLoginError] = useState(false);
    const [searchParams] = useSearchParams();

    const initialEmail = searchParams.get('email') || undefined;
    const initialFirstName = searchParams.get('first-name') || undefined;
    const initialLastName = searchParams.get('last-name') || undefined;
    const product = parseQueryProduct(searchParams.get('products'));

    const resumeUrl = searchParams.get('resumeUrl') || undefined;
    const resumeUrlDashed = searchParams.get('resume-url') || undefined;

    const isFBOnline = product === Product.FBOnline;
    const isPublisher2 = product === Product.Publisher2;

    const {
        attempts: captchaAttempts,
        enable: enableCaptcha,
        id: captchaId,
        imageSrc: captchaImageSrc,
        refresh: refreshCaptcha,
    } = useCaptcha({
        initialEnabled: initialCaptchaVisible,
        width: captchaWidth,
        height: captchaHeight,
    });

    const {
        register,
        handleSubmit,
        trigger: triggerValidation,
        setValue,
        getValues,
        setError,
        watch,
        formState: { isSubmitting, isSubmitted, errors },
    } = useForm<FormDataType>({
        defaultValues: {
            firstName: initialFirstName,
            lastName: initialLastName,
            email: initialEmail,
        },
        shouldFocusError: false,
        criteriaMode: 'all',
    });

    const maybeEmail = watch('email', initialEmail);

    const optionsQuery = optionsService.getQueryArgs();

    const loginUrl = getLoginUrl({
        ...optionsQuery,
        email: maybeEmail,
    });

    const restoreUrl = getRestoreUrl({
        ...optionsQuery,
        email: maybeEmail,
    });

    const passwordTrim = useCallback(() => {
        setValue('password', getValues().password.trim());
    }, [setValue, getValues]);

    const productLink = useCallback(
        (product: Product) => {
            const newParams = createSearchParams(searchParams);
            newParams.set('products', product);
            newParams.delete('resumeUrl');
            newParams.delete('resume-url');

            const to: To = {
                search: newParams.toString(),
            };
            return to;
        },
        [searchParams],
    );

    const showCaptcha = useCallback(() => enableCaptcha(), [enableCaptcha]);

    const showCaptchaError = useCallback(() => {
        void setValue('captcha', '');
        refreshCaptcha();
        void triggerValidation();
    }, [setValue, triggerValidation, refreshCaptcha]);

    const showPolicyError = useCallback(() => {
        setError('password', {
            type: 'server',
            message: loc.Validation.PasswordPolicyErrorMessage,
        });
    }, [setError]);

    const onLoginFail = useCallback(() => {
        setHasLoginError(true);
        setError('email', {
            type: 'required',
        });
    }, [setError]);

    const handleRegister = useCallback(
        async ({ email, password }: { email: string; password: string }) => {
            const loginResponse = await sso.login({
                email,
                password,
            });
            if (!loginResponse.Success) {
                throw new Error('Assertion failed: unable to get token right after registration');
            }

            await onRegister?.();
            await sso.return({
                ...loginResponse,
                IsNewUserCreated: true,
                captchaAttempts,
                product,
                resumeUrl,
                'resume-url': resumeUrlDashed,
            });
        },
        [captchaAttempts, onRegister, product, resumeUrl, resumeUrlDashed],
    );

    const submitForm = useEventCallback(async (formData: FormDataType) => {
        const data = {
            ...formData,
            password: formData.password.trim(),
        };

        const formValid = await triggerValidation();
        if (!formValid) {
            return;
        }

        try {
            passwordTrim();
            const loginResponse = await sso.login({
                email: data.email,
                password: data.password,
                persist: data.persist,
            });

            if (loginResponse.Success) {
                await onLogin?.();
                await sso.return({
                    ...loginResponse,
                    IsNewUserCreated: false,
                });
                return;
            }
        } catch {
            // do nothing
        }

        const registerResponse = await sso.register({
            ...data,
            captchaId,
        });

        switch (registerResponse) {
            case SignupResult.SIGNUP: {
                await handleRegister(data);
                break;
            }
            case SignupResult.CAPTCHA: {
                getValues().captcha ? showCaptchaError() : showCaptcha();
                break;
            }
            case SignupResult.LOGIN_REQUIRED: {
                onLoginFail();
                break;
            }
            case SignupResult.PASSWORD_POLICY: {
                showPolicyError();
                break;
            }
            case SignupResult.ERROR: // fallthrough
            default:
                throw new Error('Unexpected error during signup');
        }
    });

    const onFormSubmit = useCallback(
        (ev: React.BaseSyntheticEvent) => {
            void handleSubmit(submitForm, passwordTrim)(ev);
        },
        [handleSubmit, passwordTrim, submitForm],
    );

    const preventLinkActionOnFormProgress = useCallback(
        (ev: React.MouseEvent<HTMLAnchorElement>) => {
            if (isSubmitting) {
                ev.preventDefault();
            }
        },
        [isSubmitting],
    );

    useEffect(() => {
        if (captchaAttempts > 0) {
            void onShowCaptcha?.(captchaAttempts);
        }
    }, [captchaAttempts, onShowCaptcha]);

    return {
        product,
        isPublisher2,
        isFBOnline,
        hasLoginError,
        loginUrl,
        restoreUrl,
        watch,
        register,
        errors,
        isSubmitting,
        isSubmitted,
        captchaImageSrc,
        captchaId,
        refreshCaptcha,
        productLink,
        onFormSubmit,
        validationRules,
        triggerValidation,
        preventLinkActionOnFormProgress,
    };
}
